import React from 'react';
import ReportButton from '../../../../components/report/ReportButton';
import * as XLSX from 'xlsx';
import { useCompanyContext } from '../../../../context/CompanyContext';
import { useWatch } from 'react-hook-form';
import { BASE_PDF_STYLES } from '../../../../../utils/reportUtils';
import { excelCleanTable } from '@/src/utils/excelUtils';
import { SubcontractChangeOrderArgs } from '../../../../Interfaces/Accounting/ACForms.interfaces';
import { useQuery } from '@tanstack/react-query';
import {
  fecthSCVendors,
  fetchSubcontracts,
} from '../../../../services/accounting/SubcontractsService';

type SLChangeOrderReportProps = {
  elementRef: React.MutableRefObject<HTMLElement | null>;
  tableID: string;
  isTextButton?: boolean;
};

const SLChangeOrderReport = ({
  elementRef,
  tableID,
  isTextButton,
}: SLChangeOrderReportProps) => {
  const { selectedCompany } = useCompanyContext();
  const sl = useWatch<SubcontractChangeOrderArgs>({ name: 'sl' }) as string;
  const reportDate = new Date();

  const getSubcontracts = useQuery({
    queryKey: ['getSubcontracts', selectedCompany],
    queryFn: ({ signal }) =>
      fetchSubcontracts(selectedCompany!.id, undefined, [0], signal),
    refetchOnWindowFocus: false,
    enabled: false,
  });

  const selectedSubcontract: {
    SL: string;
    job: string;
    jobDescription: string;
    retainage: number;
  } | null = getSubcontracts.data?.find(
    (subcontract: {
      SL: string;
      job: string;
      jobDescription: string;
      retainage: number;
    }) => subcontract.SL === sl
  );

  const vendorRequest = useQuery({
    queryKey: [
      'getSubcontractVendor',
      selectedCompany,
      sl,
      selectedSubcontract?.job,
    ],
    queryFn: ({ signal }) => {
      return fecthSCVendors(
        {
          companyID: selectedCompany!.id,
          job: selectedSubcontract?.job ?? '',
          subcontract: sl ?? '',
        },
        signal
      );
    },
    refetchOnWindowFocus: false,
    enabled: false,
  });

  const vendor = (vendorRequest.data || [])[0];

  const excelElementPreProcess = (element: HTMLElement) => {
    excelCleanTable(element);

    return element;
  };

  const excelBeforeDownload = (workBook: XLSX.WorkBook) => {
    const sheet = workBook.Sheets['Sheet1'];
    const companyName = selectedCompany!.name;

    sheet['C2'] = { t: 's', v: companyName };
    sheet['C3'] = { t: 's', v: 'SL Change Order' };

    sheet['A6'] = { t: 's', v: 'Subcontract #:' };
    sheet['B6'] = { t: 's', v: sl };

    sheet['A7'] = { t: 's', v: 'Job:' };
    sheet['B7'] = {
      t: 's',
      v: `${selectedSubcontract?.job} - ${selectedSubcontract?.jobDescription}`,
    };

    sheet['A8'] = { t: 's', v: 'Vendor:' };
    sheet['B8'] = { t: 's', v: `${vendor?.Vendor} - ${vendor?.Name}` };

    sheet['A9'] = { t: 's', v: 'Created at:' };
    sheet['B9'] = { t: 's', v: reportDate.toLocaleString() };
  };

  return (
    <ReportButton
      elementToPrintRef={elementRef}
      fileName="SL_Change_Order"
      pdfStyles={BASE_PDF_STYLES}
      excelPreprocess={excelElementPreProcess}
      excelBeforeDownload={excelBeforeDownload}
      tableOriginCell="A12"
      widthElementID={tableID}
      noButton={isTextButton}
    />
  );
};

export default SLChangeOrderReport;
